import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Input, Switch, cn } from 'rizzui';
import { toast } from 'sonner';
import moment from 'moment';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import ModalForm from '../../../../../components/forms/ModalForm';
import FileUpload from '../../../../../components/FileUpload/FileUpload';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';
import CustomTable from '../../../../../components/table/CustomTable';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import useFundaeGroupings from '../../../../../hooks/api-calls/useFundaeGroupings';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { RootState } from '../../../../../redux/store';
import { menuRoutes } from '../../../../../router/menu';
import { ClientService } from '../../../../../services/client/clientService';
import { ClientApiResponse } from '../../../../../type/entities/client-type';
import { StudentsApiResponse } from '../../../../../type/entities/student-type';
import ClientProfileLayout from '../../ClientProfileLayout';
import CreditUpdateDocumentsModal from './CreditUpdateDocumentsModal';
import {
    CREDIT_DOCUMENTS_UPLOAD_TITLE,
    validateCreditDocumentsRequired,
} from '../../../utils/creditDocumentsValidation';

interface ClientCreditsHistoryProps { }
interface ClientProfileLocationState {
    fromContract?: boolean;
    organisingCorporationId?: string | null;
    returnToContractPath?: string;
}

interface CreditPreviewContract {
    id: string;
    code: string;
}

interface CreditIncidencePreview {
    incidencesToGenerate: number;
    contracts: CreditPreviewContract[];
}

interface SelectOption {
    value: string;
    label: string;
    organisingCorporationId?: string | null;
}

const columnOrder = [
    'credits',
    'fundaeGrouping',
    'startDate',
    'endDate',
    'user',
    'createdAt',
];

const ClientCreditsHistory: React.FC<ClientCreditsHistoryProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const location = useLocation();
    const { navigationClientId, navigationOrganisingCorporationId } = useSelector((state: RootState) => state.clientProfile);
    const { handleErrors } = useHandleErrors();
    const { userCan, isFacilitatorManager } = usePrivilege();
    const canViewContracts = userCan('get_contracts', 'contracts');
    const canManageCreditUpdateDocuments = userCan('add_client_credits', 'clients') && !isFacilitatorManager;
    const canEditCreditUpdates = userCan('edit_clients', 'clients') && !isFacilitatorManager;
    const canAddCredits = userCan('add_client_credits', 'clients') && !isFacilitatorManager;
    const profileLocationState = (location.state as ClientProfileLocationState | null) ?? null;
    const fallbackOrganisingCorporationId = profileLocationState?.organisingCorporationId
        ?? (navigationClientId === id ? navigationOrganisingCorporationId : null);
    const { getFundaeGroupingsList, updateFundaeGroupingsHookFilters } = useFundaeGroupings(
        fallbackOrganisingCorporationId ? { organising_corporation_id: fallbackOrganisingCorporationId } : {}
    );
    const service = new ClientService();

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters({ client: id });

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
    const [credits, setCredits] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [fundaeGroupingId, setFundaeGroupingId] = useState<string>('');
    const [selectedContractIds, setSelectedContractIds] = useState<string[]>([]);
    const [clientContractsOptions, setClientContractsOptions] = useState<SelectOption[]>([]);
    const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(false);
    const [sendIncidenceEmails, setSendIncidenceEmails] = useState<boolean>(false);
    const [creditIncidencePreview, setCreditIncidencePreview] = useState<CreditIncidencePreview | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [openDocumentsModal, setOpenDocumentsModal] = useState<boolean>(false);
    const [selectedCreditUpdateId, setSelectedCreditUpdateId] = useState<string | null>(null);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [selectedEditCreditUpdate, setSelectedEditCreditUpdate] = useState<any | null>(null);
    const [editCredits, setEditCredits] = useState<string>('');
    const [editSelectedContractIds, setEditSelectedContractIds] = useState<string[]>([]);
    const [openEditConfirmModal, setOpenEditConfirmModal] = useState<boolean>(false);
    const [editCreditIncidencePreview, setEditCreditIncidencePreview] = useState<CreditIncidencePreview | null>(null);
    const [editSendIncidenceEmails, setEditSendIncidenceEmails] = useState<boolean>(false);
    const [creditUpdateFiles, setCreditUpdateFiles] = useState<File[]>([]);
    const [editCreditUpdateFiles, setEditCreditUpdateFiles] = useState<File[]>([]);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.listCreditsUpdate(filters);
        return response.getResponseData() as ClientApiResponse;
    }, [id, filters]));

    const getDefaultCreditDateRange = () => {
        const currentYear = new Date().getFullYear();
        return {
            startDate: `${currentYear}-01-01`,
            endDate: `${currentYear}-12-31`,
        };
    };

    const resetCreditForm = () => {
        setCredits('');
        setStartDate('');
        setEndDate('');
        setFundaeGroupingId('');
        setSelectedContractIds([]);
        setSendIncidenceEmails(false);
        setCreditIncidencePreview(null);
        setCreditUpdateFiles([]);
    };

    const closeCreditModal = () => {
        setOpenModal(false);
        resetCreditForm();
    };

    const closeConfirmModal = (reopenCreditModal: boolean = false) => {
        setOpenConfirmModal(false);
        setCreditIncidencePreview(null);
        setSendIncidenceEmails(false);
        if (reopenCreditModal) {
            setOpenModal(true);
        }
    };

    const openCreditUpdateDocumentsModal = (creditUpdate: any) => {
        setSelectedCreditUpdateId(creditUpdate?.id || null);
        setOpenDocumentsModal(true);
    };

    const closeCreditUpdateDocumentsModal = () => {
        setOpenDocumentsModal(false);
        setSelectedCreditUpdateId(null);
    };

    const selectedCreditUpdate = data?.data?.find((creditUpdate: any) => creditUpdate?.id === selectedCreditUpdateId) || null;

    const getFormattedDateValue = (value: any): string => {
        if (!value) return '';
        const raw = value?.date || value;
        const formattedDate = moment(raw);
        return formattedDate.isValid() ? formattedDate.format('YYYY-MM-DD') : '';
    };

    const loadClientContractsForCreditsUpdate = async () => {
        if (!id) {
            return;
        }

        setIsLoadingContracts(true);
        try {
            const response = await (await service.listContracts({
                page: 1,
                limit: 999999,
                filter_filters: { client: id },
                filter_order: [{ field: 'createdAt', order: 'desc' }],
            })).getResponseData();

            if (!response?.success) {
                handleErrors(response);
                setClientContractsOptions([]);
                return;
            }

            const mappedOptions = Array.isArray(response?.data?.data)
                ? response.data.data
                    .filter((contract: any) => contract?.id && contract?.code)
                    .map((contract: any) => ({
                        value: contract.id,
                        label: contract.code,
                        organisingCorporationId: contract?.organisingCorporation?.id || contract?.organisingCorporationId || null,
                    }))
                : [];

            setClientContractsOptions(mappedOptions);
        } catch (error: any) {
            toast.error(error?.message ?? 'No se pudieron cargar los contratos del cliente');
            setClientContractsOptions([]);
        } finally {
            setIsLoadingContracts(false);
        }
    };

    const openCreditModal = () => {
        const defaults = getDefaultCreditDateRange();
        setCredits('');
        setStartDate(defaults.startDate);
        setEndDate(defaults.endDate);
        setFundaeGroupingId('');
        setSelectedContractIds([]);
        setSendIncidenceEmails(false);
        setCreditIncidencePreview(null);
        loadClientContractsForCreditsUpdate();
        setOpenModal(true);
    };

    const closeEditModal = () => {
        setOpenEditModal(false);
        setSelectedEditCreditUpdate(null);
        setEditCredits('');
        setEditSelectedContractIds([]);
        setEditSendIncidenceEmails(false);
        setEditCreditIncidencePreview(null);
        setEditCreditUpdateFiles([]);
    };

    const closeEditConfirmModal = (reopenEditModal: boolean = false) => {
        setOpenEditConfirmModal(false);
        setEditSendIncidenceEmails(false);
        setEditCreditIncidencePreview(null);
        if (reopenEditModal) {
            setOpenEditModal(true);
        }
    };

    const openEditCreditModal = async (creditUpdate: any) => {
        const defaultContractIds = Array.isArray(creditUpdate?.contractIds) && creditUpdate.contractIds.length > 0
            ? creditUpdate.contractIds
            : (Array.isArray(creditUpdate?.linkedContractIds) ? creditUpdate.linkedContractIds : []);

        setSelectedEditCreditUpdate(creditUpdate);
        setEditCredits(String(creditUpdate?.credits ?? ''));
        setEditSelectedContractIds(defaultContractIds);
        setEditSendIncidenceEmails(false);
        setEditCreditIncidencePreview(null);
        setEditCreditUpdateFiles([]);
        await loadClientContractsForCreditsUpdate();
        setOpenEditModal(true);
    };

    const validateFundaeGroupingAssignment = (): boolean => {
        const hasFundaeGrouping = Boolean(fundaeGroupingId);
        const hasContractsSelected = selectedContractIds.length > 0;

        if (hasFundaeGrouping !== hasContractsSelected) {
            toast.error('Debes seleccionar agrupación fundae y contrato/s para aplicar la asignación');
            return false;
        }

        return true;
    };

    const uploadDocumentsToCreditUpdate = async (clientCreditId: string, files: File[]) => {
        if (!clientCreditId || files.length === 0) {
            return true;
        }

        const uploadResponse = await (await service.addCreditsUpdateDocuments(clientCreditId, files)).getResponseData();
        if (!uploadResponse?.success) {
            handleErrors(uploadResponse);
            return false;
        }

        return true;
    };

    const submitCreditsUpdate = async (parsedCredits: number, sendIncidenceEmailsValue: boolean) => {
        const response = await (await service.addCredits(
            id,
            parsedCredits,
            startDate,
            endDate,
            sendIncidenceEmailsValue,
            fundaeGroupingId || undefined,
            selectedContractIds,
            creditUpdateFiles
        )).getResponseData();
        if (response.success) {
            setOpenConfirmModal(false);
            closeCreditModal();
            toast.success('Créditos actualizados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const addCredits = async () => {
        const parsedCredits = parseFloat(credits);
        if (credits.trim() === '' || Number.isNaN(parsedCredits)) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }

        if (parsedCredits < 0) {
            toast.error('La cantidad de créditos no puede ser negativa');
            return;
        }

        if (!validateFundaeGroupingAssignment()) {
            return;
        }

        if (!validateCreditDocumentsRequired(creditUpdateFiles)) {
            return;
        }

        setIsLoading(true);
        const previewResponse = await (await service.previewAddCredits(
            id,
            parsedCredits,
            startDate,
            endDate,
            fundaeGroupingId || undefined,
            selectedContractIds
        )).getResponseData();
        if (!previewResponse.success) {
            handleErrors(previewResponse);
            setIsLoading(false);
            return;
        }

        const previewData = previewResponse.data || {};
        if (previewData.willGenerateIncidences) {
            const contracts: CreditPreviewContract[] = Array.isArray(previewData.contracts)
                ? previewData.contracts
                    .filter((contract: any) => contract && typeof contract.code === 'string' && contract.code.trim() !== '')
                    .map((contract: any) => ({
                        id: typeof contract.id === 'string' ? contract.id : '',
                        code: contract.code,
                    }))
                : [];
            setCreditIncidencePreview({
                incidencesToGenerate: previewData.incidencesToGenerate || 0,
                contracts,
            });
            setOpenModal(false);
            setOpenConfirmModal(true);
            setIsLoading(false);
            return;
        }

        await submitCreditsUpdate(parsedCredits, false);
        setIsLoading(false);
    };

    const confirmCreditsUpdate = async () => {
        const parsedCredits = parseFloat(credits);
        if (credits.trim() === '' || Number.isNaN(parsedCredits) || parsedCredits < 0) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }
        if (!validateFundaeGroupingAssignment()) {
            return;
        }

        if (!validateCreditDocumentsRequired(creditUpdateFiles)) {
            return;
        }

        setIsLoading(true);
        await submitCreditsUpdate(parsedCredits, sendIncidenceEmails);
        setIsLoading(false);
    };

    const submitEditCreditsUpdate = async (parsedCredits: number, sendIncidenceEmailsValue: boolean) => {
        if (!selectedEditCreditUpdate?.id) {
            toast.error('No se ha seleccionado una actualización de créditos');
            return;
        }

        const response = await (await service.editCreditsUpdate(
            selectedEditCreditUpdate.id,
            parsedCredits,
            sendIncidenceEmailsValue,
            editSelectedContractIds
        )).getResponseData();

        if (response.success) {
            const clientCreditsUpdateId = response?.data?.clientCreditsUpdateId || selectedEditCreditUpdate.id;
            const documentsUploaded = await uploadDocumentsToCreditUpdate(clientCreditsUpdateId, editCreditUpdateFiles);

            setOpenEditConfirmModal(false);
            closeEditModal();
            if (documentsUploaded) {
                toast.success('Actualización de créditos editada correctamente');
            } else {
                toast.error('Actualización editada, pero no se pudieron adjuntar algunos documentos');
            }
            refetch();
        } else {
            handleErrors(response);
        }
    };

    const previewEditCreditsUpdate = async () => {
        const parsedCredits = parseFloat(editCredits);
        if (editCredits.trim() === '' || Number.isNaN(parsedCredits) || parsedCredits < 0) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }

        if (!selectedEditCreditUpdate) {
            toast.error('No se ha seleccionado una actualización de créditos');
            return;
        }

        const startDate = getFormattedDateValue(selectedEditCreditUpdate.startDate);
        const endDate = getFormattedDateValue(selectedEditCreditUpdate.endDate);

        setIsLoading(true);
        const previewResponse = await (await service.previewAddCredits(
            id,
            parsedCredits,
            startDate,
            endDate,
            undefined,
            editSelectedContractIds,
            selectedEditCreditUpdate.id
        )).getResponseData();

        if (!previewResponse.success) {
            handleErrors(previewResponse);
            setIsLoading(false);
            return;
        }

        const previewData = previewResponse.data || {};
        if (previewData.willGenerateIncidences) {
            const contracts: CreditPreviewContract[] = Array.isArray(previewData.contracts)
                ? previewData.contracts
                    .filter((contract: any) => contract && typeof contract.code === 'string' && contract.code.trim() !== '')
                    .map((contract: any) => ({
                        id: typeof contract.id === 'string' ? contract.id : '',
                        code: contract.code,
                    }))
                : [];
            setEditCreditIncidencePreview({
                incidencesToGenerate: previewData.incidencesToGenerate || 0,
                contracts,
            });

            setOpenEditModal(false);
            setOpenEditConfirmModal(true);
            setIsLoading(false);
            return;
        }

        await submitEditCreditsUpdate(parsedCredits, false);
        setIsLoading(false);
    };

    const confirmEditCreditsUpdate = async () => {
        const parsedCredits = parseFloat(editCredits);
        if (editCredits.trim() === '' || Number.isNaN(parsedCredits) || parsedCredits < 0) {
            toast.error('Debes indicar una cantidad de créditos válida');
            return;
        }

        setIsLoading(true);
        await submitEditCreditsUpdate(parsedCredits, editSendIncidenceEmails);
        setIsLoading(false);
    };

    /**
     * Order the data to match the column order
     */
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {
            ...row,
            id: row.id,
            documents: row.documents,
            contractIds: row.contractIds || [],
            linkedContractIds: row.linkedContractIds || [],
        };
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    useEffect(() => {
        const selectedContracts = clientContractsOptions.filter((option) => selectedContractIds.includes(option.value));
        const uniqueOrganisingCorporationIds = Array.from(
            new Set(
                selectedContracts
                    .map((option) => option.organisingCorporationId)
                    .filter((value): value is string => Boolean(value))
            )
        );

        updateFundaeGroupingsHookFilters({
            organising_corporation_id: uniqueOrganisingCorporationIds.length === 1
                ? uniqueOrganisingCorporationIds[0]
                : uniqueOrganisingCorporationIds.length === 0
                    ? fallbackOrganisingCorporationId
                    : null
        });
    }, [selectedContractIds, clientContractsOptions, fallbackOrganisingCorporationId, updateFundaeGroupingsHookFilters]);

    return (
        <ClientProfileLayout>
            {data !== undefined && (
                <div className="pt-4">
                    <CustomTable
                        id={'credits-table'}
                        columnOrder={columnOrder}
                        filter={false}
                        data={orderedData()}
                        isLoading={loading}
                        columnsNotShown={['id', 'comment', 'contractIds', 'linkedContractIds', 'company', 'client', 'updatedAt', 'documents']}
                                overrideColumns={[
                            {
                                key: 'fundaeGrouping',
                                render: (row: any) => {
                                    const fundaeGrouping = row.row.original.fundaeGrouping;
                                    return fundaeGrouping?.name || 'N/A';
                                },
                            },
                            {
                                key: 'startDate',
                                render: (row: any) => {
                                    const date = row.row.original.startDate?.date || row.row.original.startDate;
                                    return moment(date).isValid() ? moment(date).format('DD/MM/YYYY') : 'N/A';
                                },
                            },
                            {
                                key: 'endDate',
                                render: (row: any) => {
                                    const date = row.row.original.endDate?.date || row.row.original.endDate;
                                    return moment(date).isValid() ? moment(date).format('DD/MM/YYYY') : 'N/A';
                                },
                            },
                            {
                                key: 'user',
                                render: (row: any) => {
                                    const user = row?.row?.original?.user;
                                    if (!user?.name) {
                                        return 'N/A';
                                    }

                                    return `${user.name} ${user.lastName || ''}`.trim();
                                },
                            },
                                ]}
                                actions={[
                                    ...(canEditCreditUpdates ? [{
                                        label: 'Editar',
                                        render: (row: any) => {
                                            const rowData = row?.row?.original || row;
                                            return (
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => openEditCreditModal(rowData)}
                                                    disabled={!rowData?.id}
                                                >
                                                    Editar
                                                </Button>
                                            );
                                        },
                                    }] : []),
                                    ...(canManageCreditUpdateDocuments ? [{
                                        label: 'Documentos',
                                        render: (row: any) => {
                                            const rowData = row?.row?.original || row;
                                            return (
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => openCreditUpdateDocumentsModal(rowData)}
                                                    disabled={!rowData?.id}
                                                >
                                                    Documentos
                                                </Button>
                                            );
                                        },
                                    }] : []),
                                ]}
                                filters={filters}
                                updateFilters={updateFilters}
                        updateFilterOrder={updateFilterOrder}
                        defaultOrder={filters.filter_order || undefined}
                        paginationData={{
                            pageSize: filters.limit,
                            currentPage: filters.page,
                            pageCount: (data as StudentsApiResponse) ? data.lastPage : 1,
                            totalCount: data?.totalRegisters,
                            handlePagination: updatePage,
                            handlePerPage: updatePageSize,
                        }}
                        toggleFilters={() => setOpenFilters(!openFilters)}
                    >
                        {canAddCredits && (
                            <Button
                                color="primary"
                                onClick={openCreditModal}
                                className={cn(['mt-4 md:mt-0'])}
                                size="sm"
                            >
                                Añadir créditos
                            </Button>
                        )}
                    </CustomTable>
                </div>
            )}

            <ModalForm isOpen={openModal} onClose={closeCreditModal} title='Añadir actualización de créditos'>
                <Input
                    label='Créditos'
                    type='number'
                    value={credits}
                    onChange={(e) => setCredits(e.target.value)}
                />

                <Input
                    label='Fecha de inicio de validez de los créditos'
                    type='date'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className='mt-3'
                />

                <Input
                    label='Fecha de fin de validez de los créditos'
                    type='date'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className='mt-3'
                />

                <div className='pt-4'>
                    <CustomSelect
                        id='fundaeGroupingId'
                        label='Agrupación Fundae'
                        options={getFundaeGroupingsList()}
                        value={fundaeGroupingId
                            ? getFundaeGroupingsList().find((grouping: SelectOption) => grouping.value === fundaeGroupingId) || null
                            : null}
                        onChange={(value: any) => setFundaeGroupingId(value?.value || '')}
                        placeholder='Selecciona agrupación fundae'
                        isSearchable
                        isClearable
                    />
                </div>

                <div className='pt-4'>
                    <CustomSelect
                        id='contractIds'
                        label='Contrato/s a aplicar'
                        options={clientContractsOptions}
                        value={clientContractsOptions.filter((option) => selectedContractIds.includes(option.value))}
                        onChange={(value: any) => {
                            const ids = Array.isArray(value) ? value.map((item) => item.value) : [];
                            setSelectedContractIds(ids);
                        }}
                        placeholder='Selecciona contrato/s'
                        isSearchable
                        isClearable
                        isMulti
                        isLoading={isLoadingContracts}
                    />
                </div>

                <div className='pt-4'>
                    <FileUpload
                        files={creditUpdateFiles}
                        onFilesChange={setCreditUpdateFiles}
                        title={CREDIT_DOCUMENTS_UPLOAD_TITLE}
                        dropText='Arrastra archivos aquí o haz clic para seleccionarlos'
                        dragActiveText='Suelta los archivos aquí...'
                        maxFiles={50}
                        maxSize={52428800}
                        showFileList
                        requiredAsterisk={true}
                    />
                </div>

                <div className='flex justify-center p-7'>
                    <Button
                        disabled={isLoading || !startDate || !endDate || credits.trim() === '' || creditUpdateFiles.length === 0}
                        onClick={() => addCredits()}
                        color='primary'
                    >
                        Añadir créditos
                    </Button>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={openConfirmModal}
                onClose={() => closeConfirmModal(true)}
                title='Confirmar actualización de créditos'
            >
                <div className='space-y-4 pb-6'>
                    <p className='text-sm text-gray-700'>
                        Esta actualización generará {creditIncidencePreview?.incidencesToGenerate || 0} incidencia(s). ¿Deseas continuar?
                    </p>

                    {creditIncidencePreview?.contracts && creditIncidencePreview.contracts.length > 0 && (
                        <div>
                            <p className='text-sm font-medium mb-2'>Contratos vinculados</p>
                            <div className='flex flex-wrap gap-2'>
                                {creditIncidencePreview.contracts.map((contract, index) => (
                                    canViewContracts && contract.id
                                        ? (
                                            <Link
                                                key={contract.id}
                                                to={`${menuRoutes.contracts.path}/${contract.id}/profile${menuRoutes.contracts.profile.info}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-primary hover:bg-gray-50'
                                            >
                                                {contract.code}
                                            </Link>
                                        )
                                        : (
                                            <span
                                                key={`${contract.id || 'contract'}-${contract.code}-${index}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-700'
                                            >
                                                {contract.code}
                                            </span>
                                        )
                                ))}
                            </div>
                        </div>
                    )}

                    <div className='pt-2'>
                        <Switch
                            label='Enviar correo a responsables/participantes si se registran incidencias'
                            checked={sendIncidenceEmails}
                            onChange={() => setSendIncidenceEmails(!sendIncidenceEmails)}
                            switchKnobClassName='bg-secondary'
                            switchClassName='w-[65px]'
                        />
                    </div>

                    <div className='flex justify-end gap-3 pt-2'>
                        <Button variant='outline' onClick={() => closeConfirmModal(true)} disabled={isLoading}>
                            Cancelar
                        </Button>
                        <Button color='primary' onClick={confirmCreditsUpdate} isLoading={isLoading} disabled={isLoading}>
                            Confirmar y actualizar
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={openEditModal}
                onClose={closeEditModal}
                title='Editar actualización de créditos'
            >
                <Input
                    label='Créditos'
                    type='number'
                    value={editCredits}
                    onChange={(e) => setEditCredits(e.target.value)}
                />

                <div className='pt-4'>
                    <CustomSelect
                        id='editContractIds'
                        label='Contrato/s a aplicar'
                        options={clientContractsOptions}
                        value={clientContractsOptions.filter((option) => editSelectedContractIds.includes(option.value))}
                        onChange={(value: any) => {
                            const ids = Array.isArray(value) ? value.map((item) => item.value) : [];
                            setEditSelectedContractIds(ids);
                        }}
                        placeholder='Selecciona contrato/s'
                        isSearchable
                        isClearable
                        isMulti
                        isLoading={isLoadingContracts}
                    />
                </div>

                <div className='pt-4'>
                    <FileUpload
                        files={editCreditUpdateFiles}
                        onFilesChange={setEditCreditUpdateFiles}
                        title='Documentos de la actualización'
                        dropText='Arrastra archivos aquí o haz clic para seleccionarlos'
                        dragActiveText='Suelta los archivos aquí...'
                        maxFiles={50}
                        maxSize={52428800}
                        showFileList
                    />
                </div>

                <div className='flex justify-center p-7'>
                    <Button
                        disabled={isLoading || editCredits.trim() === ''}
                        onClick={previewEditCreditsUpdate}
                        color='primary'
                    >
                        Guardar cambios
                    </Button>
                </div>
            </ModalForm>

            <ModalForm
                isOpen={openEditConfirmModal}
                onClose={() => closeEditConfirmModal(true)}
                title='Confirmar edición de créditos'
            >
                <div className='space-y-4 pb-6'>
                    <p className='text-sm text-gray-700'>
                        Esta edición generará {editCreditIncidencePreview?.incidencesToGenerate || 0} incidencia(s). ¿Deseas continuar?
                    </p>

                    {editCreditIncidencePreview?.contracts && editCreditIncidencePreview.contracts.length > 0 && (
                        <div>
                            <p className='text-sm font-medium mb-2'>Contratos vinculados</p>
                            <div className='flex flex-wrap gap-2'>
                                {editCreditIncidencePreview.contracts.map((contract, index) => (
                                    canViewContracts && contract.id
                                        ? (
                                            <Link
                                                key={contract.id}
                                                to={`${menuRoutes.contracts.path}/${contract.id}/profile${menuRoutes.contracts.profile.info}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-primary hover:bg-gray-50'
                                            >
                                                {contract.code}
                                            </Link>
                                        )
                                        : (
                                            <span
                                                key={`${contract.id || 'contract'}-${contract.code}-${index}`}
                                                className='inline-flex items-center rounded-md border border-gray-200 px-3 py-1 text-sm font-semibold text-gray-700'
                                            >
                                                {contract.code}
                                            </span>
                                        )
                                ))}
                            </div>
                        </div>
                    )}

                    <div className='pt-2'>
                        <Switch
                            label='Enviar correo a responsables/participantes si se registran incidencias'
                            checked={editSendIncidenceEmails}
                            onChange={() => setEditSendIncidenceEmails(!editSendIncidenceEmails)}
                            switchKnobClassName='bg-secondary'
                            switchClassName='w-[65px]'
                        />
                    </div>

                    <div className='flex justify-end gap-3 pt-2'>
                        <Button variant='outline' onClick={() => closeEditConfirmModal(true)} disabled={isLoading}>
                            Cancelar
                        </Button>
                        <Button color='primary' onClick={confirmEditCreditsUpdate} isLoading={isLoading} disabled={isLoading}>
                            Confirmar edición
                        </Button>
                    </div>
                </div>
            </ModalForm>

            <CreditUpdateDocumentsModal
                isOpen={openDocumentsModal}
                onClose={closeCreditUpdateDocumentsModal}
                creditUpdate={selectedCreditUpdate}
                onDocumentsChanged={refetch}
            />
        </ClientProfileLayout>
    );
};

export default ClientCreditsHistory;
